import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, CheckCircle2, Zap } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import MonthPicker from '../../components/ui/MonthPicker/MonthPicker';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getCommissions, finalizeCommission } from '../../services/api/callCenterCommission';
import styles from './CommissionListPage.module.css';

const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const CommissionListPage = () => {
  const { t, formatCurrency } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const canFinalize = hasPermission('finalize_callcenter_commission');

  const [commissions, setCommissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeMonth, setFinalizeMonth] = useState(previousMonth());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ month: '' });

  const loadCommissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (filters.month) params.month = filters.month;
      const data = await getCommissions(params);
      setCommissions(data.commissions || []);
    } catch (err) {
      console.error('Error loading commissions:', err);
      showError(err.response?.data?.message || t('callCenterCommission.failedToLoadCommissions'));
    } finally {
      setIsLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  const handleFinalize = async () => {
    const settlementMonth = finalizeMonth;
    if (!settlementMonth) return;
    const ok = await confirm(
      t('callCenterCommission.finalizeConfirmMessage').replace('{month}', settlementMonth),
      { confirmText: t('callCenterCommission.finalize'), cancelText: t('common.cancel') }
    );
    if (!ok) return;

    setIsFinalizing(true);
    try {
      await finalizeCommission(settlementMonth);
      success(t('callCenterCommission.finalizeSuccess'));
      loadCommissions();
    } catch (err) {
      console.error('Error finalizing commission:', err);
      showError(err.response?.data?.message || t('callCenterCommission.finalizeFailed'));
    } finally {
      setIsFinalizing(false);
    }
  };

  const filterDefinitions = [
    {
      key: 'month',
      label: t('callCenterCommission.filterByMonth'),
      type: 'custom',
      render: (value, onChange) => (
        <MonthPicker value={value} onChange={onChange} placeholder={t('callCenterCommission.allMonths')} />
      ),
    },
  ];

  const columns = [
    { key: 'settlementMonth', header: t('callCenterCommission.settlementMonth'), width: '140px', render: (row) => row.settlementMonth },
    {
      key: 'totalCommission',
      header: t('callCenterCommission.totalCommission'),
      width: '160px',
      render: (row) => formatCurrency(row.totalCommission),
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => (
        <span className={row.status === 'completed' ? styles.completedBadge : styles.pendingBadge}>
          {row.status === 'completed' ? <CheckCircle2 size={12} /> : <Zap size={12} />}
          {t(`callCenterCommission.status.${row.status}`)}
        </span>
      ),
    },
    {
      key: 'finalizedAt',
      header: t('callCenterCommission.finalizedAt'),
      width: '160px',
      render: (row) => new Date(row.finalizedAt).toLocaleString(),
    },
  ];

  return (
    <div className={styles.commissionListPage}>
      <PageHeader
        title={t('callCenterCommission.pageTitle')}
        description={t('callCenterCommission.pageDescription')}
        icon={Wallet}
        actions={
          canFinalize && (
            <div className={styles.finalizeActions}>
              <MonthPicker
                value={finalizeMonth}
                onChange={setFinalizeMonth}
                placeholder={t('callCenterCommission.selectMonthToFinalize')}
              />
              <button onClick={handleFinalize} className={styles.primaryButton} disabled={isFinalizing || !finalizeMonth}>
                <Zap size={20} />
                {isFinalizing ? t('common.processing') : t('callCenterCommission.finalizeMonth')}
              </button>
            </div>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={setFilters}
        onClear={() => setFilters({ month: '' })}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
      />

      <DataTable
        data={commissions}
        columns={columns}
        onRowClick={(row) => navigate(`/call-center/commission/${row.id}`)}
        loading={isLoading}
        emptyMessage={t('callCenterCommission.noCommissionsYet')}
        emptyDescription={t('callCenterCommission.noCommissionsYetDescription')}
        emptyIcon={<Wallet size={64} />}
      />
    </div>
  );
};

export default CommissionListPage;
